<template>
    <div v-if="trip" class="post-selection">
        <Requirement class="post-selection-requirement" :trip="trip" :onScroll="onClose" />
        <TransferDetails :transfer="product" />
        <Divider color="lightGrey" thickness="1" topMargin="xl" bottomMargin="xl" />
        <TransferDescription :transfer="product" />
        <Divider color="lightGrey" thickness="1" topMargin="xl" bottomMargin="xl" />
        <div class="post-selection-footer">
            <ButtonPrimary
                :onClick="onSaveTransfer"
                :loading="isUpdating"
                :disabled="disabled">
                {{ t({ key: 'shared.add_transfer' }) }}
            </ButtonPrimary>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, computed, PropType } from 'vue';
    import { useStore } from '@/store';
    import { t } from '@shared/helpers/translation';
    import {
        BookingOrigin,
        Cart,
        ProductType,
        TransferProduct,
    } from '@/api/Cart/cart.interfaces';
    import { logStatsigCheckoutTransferAdded } from '@/tracking/statsig/checkout.functions';
    import { getErrorMessage, handleCustomError } from '@/config/errorHandling';
    import { isEmpty } from 'lodash';
    import { trackClarity } from '@/tracking/clarity';
    import { TransferTrip } from '@/helpers/transfer/transfer.interfaces';
    import { ClarityEvent } from '@/tracking/clarity/types';
    import { endpoints } from '@/api/endpoints';
    import Requirement from '../Shared/Requirement.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import TransferDescription from '@/components/checkoutPageV3/Sections/Addons/Transfer/Trips/Trip/Shared/TransferDescription/TransferDescription.vue';
    import Divider from '@ui/components/Divider/Divider.vue';
    import TransferDetails from '@/components/checkoutPageV3/Sections/Addons/Transfer/Trips/Trip/Shared/TransferDetails/TransferDetails.vue';

    export default defineComponent({
        name: 'TransferPostSelection',
        methods: { t },
        components: {
            Divider,
            ButtonPrimary,
            TransferDetails,
            Requirement,
            TransferDescription,
        },
        props: {
            product: { type: Object as PropType<TransferProduct>, required: true },
            trip: { type: Object as PropType<TransferTrip>, required: true },
            onSaveError: { type: Function, required: true },
            onClose: { type: Function, required: true },
        },
        setup(props) {
            const store = useStore();

            const cart = computed<Cart>(() => store.state.cart.data);

            const isUpdating = computed<boolean>(
                () => store.state.cart.status === 'UPDATING'
            );

            const disabled = computed<boolean>(() => !props.product.MainPassenger);

            async function onSaveTransfer() {
                try {
                    store.dispatch('cart/setCartUpdating');

                    const updatedCart = addTransferProduct(props.product);

                    const response = await endpoints.cart.UpdateCart(
                        updatedCart,
                        BookingOrigin.Checkout,
                        false
                    );

                    if (!isEmpty(response.ErrorMessage)) {
                        throw response.ErrorMessage;
                    } else {
                        logStatsigCheckoutTransferAdded(props.product, props.trip);
                        trackClarity(ClarityEvent.CHECKOUT_ADDON_TRANSFER_ADDED);
                        store.dispatch('cart/setCartSuccess', response);
                        props.onClose();
                    }
                } catch (e) {
                    handleCustomError(
                        `[CheckoutPage] onSaveTransfer(): ${getErrorMessage(e)}`
                    );

                    props.onSaveError();
                } finally {
                    store.dispatch('cart/resetCartUpdating');
                }
            }

            function addTransferProduct(transfer: TransferProduct): Cart {
                const updatedCart = { ...cart.value };

                const transferProducts = updatedCart.Products.filter(
                    (x) => x.Type === ProductType.Transfer
                ) as TransferProduct[];

                const restProducts = updatedCart.Products.filter(
                    (x) => x.Type !== ProductType.Transfer
                );

                updatedCart.Products = [
                    ...restProducts,
                    ...transferProducts.filter((x) => x.TripId !== transfer.TripId),
                    transfer,
                ];

                return updatedCart;
            }

            return {
                disabled,
                isUpdating,
                onSaveTransfer,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './PostSelection.scss';
</style>
